import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Zap, Crown, TrendingUp, Shield, Play, Pause, Volume2, VolumeX } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { getProducts, seedDatabase } from '../api';
import { Button } from '../components/ui/button';

const Home = () => {
  const [products, setProducts] = useState([]);
  const [hotProducts, setHotProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [videoPlaying, setVideoPlaying] = useState(true);
  const [videoMuted, setVideoMuted] = useState(true);
  const videoRef = useRef(null);
  const heroRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const heroOpacity = useTransform(scrollYProgress, [0, 1], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.2]);
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 150]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        try {
          await seedDatabase();
        } catch (e) {
          // Already seeded
        }
        
        const [allRes, hotRes] = await Promise.all([
          getProducts({ limit: 8 }),
          getProducts({ is_hot: true, limit: 4 })
        ]);
        
        setProducts(allRes.data);
        setHotProducts(hotRes.data);
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const toggleVideo = () => {
    if (videoRef.current) {
      if (videoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setVideoPlaying(!videoPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoMuted;
      setVideoMuted(!videoMuted);
    }
  };

  const features = [
    { icon: Shield, title: 'LEGIT ONLY', desc: 'Direct links to official brand stores' },
    { icon: Zap, title: 'INSTANT ACCESS', desc: 'No middlemen, cop directly' },
    { icon: Crown, title: 'PREMIUM DROPS', desc: 'Early access for members' },
    { icon: TrendingUp, title: 'PRICE ALERTS', desc: 'Never miss a deal' },
  ];

  const brands = [
    { name: 'NIKE', logo: '✓' },
    { name: 'JORDAN', logo: '🏀' },
    { name: 'PUMA', logo: '🐆' },
    { name: 'REEBOK', logo: '◊' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] overflow-hidden" data-testid="home-page">
      {/* Hero Section with Video Background */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden">
        {/* Video Background */}
        <motion.div 
          style={{ scale: heroScale, y: heroY }}
          className="absolute inset-0 z-0"
        >
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            poster="https://images.unsplash.com/photo-1552346154-21d32810aba3"
          >
            <source src="https://videos.pexels.com/video-files/4625518/4625518-uhd_2560_1440_30fps.mp4" type="video/mp4" />
          </video>
          
          {/* Dark overlay with gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
        </motion.div>

        {/* Animated Light Effects */}
        <div className="absolute inset-0 z-10 pointer-events-none overflow-hidden">
          {/* Volt green glow top-right */}
          <motion.div
            animate={{
              opacity: [0.3, 0.6, 0.3],
              scale: [1, 1.2, 1],
            }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(210, 255, 0, 0.3) 0%, transparent 70%)',
              filter: 'blur(60px)',
            }}
          />
          
          {/* Purple accent glow bottom-left */}
          <motion.div
            animate={{
              opacity: [0.2, 0.4, 0.2],
              scale: [1, 1.3, 1],
            }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className="absolute -bottom-48 -left-48 w-[500px] h-[500px] rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(147, 51, 234, 0.25) 0%, transparent 70%)',
              filter: 'blur(80px)',
            }}
          />

          {/* Animated scan line */}
          <motion.div
            animate={{ y: ['-100%', '200%'] }}
            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
            className="absolute left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#D2FF00]/50 to-transparent"
          />

          {/* Floating particles */}
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-[#D2FF00] rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -30, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>

        {/* Video Controls */}
        <div className="absolute bottom-8 right-8 z-30 flex gap-2">
          <button
            onClick={toggleVideo}
            className="p-3 bg-black/50 backdrop-blur-sm border border-white/20 text-white hover:bg-[#D2FF00] hover:text-black transition-all"
          >
            {videoPlaying ? <Pause size={18} /> : <Play size={18} />}
          </button>
          <button
            onClick={toggleMute}
            className="p-3 bg-black/50 backdrop-blur-sm border border-white/20 text-white hover:bg-[#D2FF00] hover:text-black transition-all"
          >
            {videoMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
        </div>

        {/* Hero Content */}
        <motion.div 
          style={{ opacity: heroOpacity }}
          className="relative z-20 max-w-7xl mx-auto px-6 lg:px-12 py-24"
        >
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="max-w-3xl"
          >
            {/* Animated badge */}
            <motion.span 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="inline-flex items-center gap-2 bg-[#D2FF00] text-black text-xs font-bold uppercase px-4 py-2 mb-6"
            >
              <motion.span
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
                className="w-2 h-2 bg-black rounded-full"
              />
              YOUR GATEWAY TO HYPE
            </motion.span>
            
            <h1 className="font-display text-6xl sm:text-7xl lg:text-9xl uppercase leading-none mb-6">
              <motion.span
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="block"
              >
                COP THE
              </motion.span>
              <motion.span 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="block relative"
              >
                <span className="text-[#D2FF00] relative">
                  FRESHEST
                  {/* Glowing underline */}
                  <motion.span
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: 1, duration: 0.8 }}
                    className="absolute -bottom-2 left-0 right-0 h-1 bg-[#D2FF00] origin-left"
                    style={{ boxShadow: '0 0 20px #D2FF00, 0 0 40px #D2FF00' }}
                  />
                </span>
              </motion.span>
              <motion.span
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="block"
              >
                KICKS
              </motion.span>
            </h1>
            
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="text-white/60 text-lg sm:text-xl mb-10 max-w-xl leading-relaxed"
            >
              Direct links to Nike, Jordan, Puma, Reebok & Louis Vuitton. 
              No fakes, no middlemen. Just pure heat delivered straight from official stores.
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2 }}
              className="flex flex-wrap gap-4"
            >
              <Link to="/shop">
                <Button className="btn-primary flex items-center gap-2 group" data-testid="shop-now-btn">
                  SHOP NOW
                  <motion.span
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  >
                    <ArrowRight size={18} />
                  </motion.span>
                </Button>
              </Link>
              <Link to="/premium">
                <Button className="btn-secondary flex items-center gap-2 hover:shadow-[0_0_30px_rgba(210,255,0,0.3)]">
                  <Crown size={18} />
                  GO PREMIUM
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="flex flex-col items-center gap-2"
          >
            <span className="text-white/40 text-xs uppercase tracking-widest">Scroll</span>
            <div className="w-[1px] h-12 bg-gradient-to-b from-white/40 to-transparent" />
          </motion.div>
        </motion.div>
      </section>

      {/* Brands Marquee */}
      <section className="py-8 bg-[#0A0A0A] border-y border-white/5 overflow-hidden">
        <motion.div
          animate={{ x: [0, -1000] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="flex items-center gap-16 whitespace-nowrap"
        >
          {[...brands, ...brands, ...brands].map((brand, i) => (
            <div key={i} className="flex items-center gap-4 text-white/20 hover:text-[#D2FF00] transition-colors cursor-pointer">
              <span className="text-2xl">{brand.logo}</span>
              <span className="font-display text-2xl">{brand.name}</span>
            </div>
          ))}
        </motion.div>
      </section>

      {/* Hot Drops Section with Neon Effects */}
      <section className="py-24 px-6 lg:px-12 bg-[#050505] relative">
        {/* Background glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#FF3B30]/10 blur-[150px] pointer-events-none" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex items-center justify-between mb-12"
          >
            <div>
              <motion.span 
                className="text-[#FF3B30] text-sm font-bold uppercase tracking-wider flex items-center gap-2 mb-2"
                animate={{ opacity: [1, 0.5, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Zap size={16} className="fill-current" />
                TRENDING NOW
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF3B30] opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF3B30]"></span>
                </span>
              </motion.span>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase">
                HOT <span className="text-[#FF3B30]">DROPS</span>
              </h2>
            </div>
            <Link to="/shop?is_hot=true" className="btn-ghost flex items-center gap-2 group">
              VIEW ALL
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="aspect-square skeleton" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {hotProducts.map((product, i) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <ProductCard product={product} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Features Section with Glass Cards */}
      <section className="py-24 px-6 lg:px-12 bg-[#0A0A0A] relative overflow-hidden">
        {/* Animated background grid */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(210,255,0,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(210,255,0,0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px',
          }} />
        </div>

        {/* Floating orbs */}
        <motion.div
          animate={{ y: [0, -20, 0], x: [0, 10, 0] }}
          transition={{ duration: 6, repeat: Infinity }}
          className="absolute top-20 right-20 w-32 h-32 rounded-full bg-[#D2FF00]/20 blur-3xl"
        />
        <motion.div
          animate={{ y: [0, 20, 0], x: [0, -10, 0] }}
          transition={{ duration: 8, repeat: Infinity }}
          className="absolute bottom-20 left-20 w-40 h-40 rounded-full bg-purple-500/20 blur-3xl"
        />

        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase mb-4">
              WHY <span className="text-[#D2FF00]">INDIGO SQUARE</span>?
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto text-lg">
              We're not another reseller. We connect you directly to official brand stores 
              so you can cop with confidence.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="relative group"
              >
                {/* Glass card */}
                <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 p-8 h-full overflow-hidden">
                  {/* Hover glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#D2FF00]/0 to-[#D2FF00]/0 group-hover:from-[#D2FF00]/10 group-hover:to-transparent transition-all duration-500" />
                  
                  {/* Corner accent */}
                  <div className="absolute top-0 left-0 w-16 h-[2px] bg-[#D2FF00]" />
                  <div className="absolute top-0 left-0 w-[2px] h-16 bg-[#D2FF00]" />
                  
                  <feature.icon className="text-[#D2FF00] mb-4 relative z-10" size={36} />
                  <h3 className="font-display text-xl uppercase mb-2 relative z-10">{feature.title}</h3>
                  <p className="text-white/60 text-sm relative z-10">{feature.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* New Arrivals Grid */}
      <section className="py-24 px-6 lg:px-12 bg-[#050505] relative">
        {/* Volt glow */}
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-[#D2FF00]/5 blur-[200px] pointer-events-none" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex items-center justify-between mb-12"
          >
            <div>
              <span className="text-[#D2FF00] text-sm font-bold uppercase tracking-wider mb-2 block">
                LATEST ADDITIONS
              </span>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase">NEW ARRIVALS</h2>
            </div>
            <Link to="/shop" className="btn-ghost flex items-center gap-2 group">
              BROWSE ALL
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="aspect-square skeleton" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {products.map((product, i) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                >
                  <ProductCard product={product} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section with Animated Background */}
      <section className="py-32 px-6 lg:px-12 relative overflow-hidden">
        {/* Animated gradient background */}
        <motion.div
          animate={{
            background: [
              'linear-gradient(45deg, #D2FF00 0%, #a8cc00 50%, #D2FF00 100%)',
              'linear-gradient(45deg, #a8cc00 0%, #D2FF00 50%, #a8cc00 100%)',
              'linear-gradient(45deg, #D2FF00 0%, #a8cc00 50%, #D2FF00 100%)',
            ],
          }}
          transition={{ duration: 5, repeat: Infinity }}
          className="absolute inset-0"
        />
        
        {/* Noise texture overlay */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.65\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")',
        }} />

        {/* Floating shapes */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="absolute top-10 right-10 w-40 h-40 border-4 border-black/10"
        />
        <motion.div
          animate={{ rotate: -360 }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          className="absolute bottom-10 left-10 w-32 h-32 border-4 border-black/10 rounded-full"
        />

        <div className="max-w-7xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <motion.h2 
              className="font-display text-5xl sm:text-6xl lg:text-8xl uppercase text-black mb-6"
              animate={{ y: [0, -5, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              NEVER MISS A DROP
            </motion.h2>
            <p className="text-black/70 text-lg sm:text-xl mb-10 max-w-2xl mx-auto">
              Go Premium for early access to limited releases, exclusive deals, and price drop alerts.
            </p>
            <Link to="/premium">
              <motion.button
                whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(0,0,0,0.3)' }}
                whileTap={{ scale: 0.95 }}
                className="bg-black text-white hover:bg-white hover:text-black font-bold uppercase tracking-wider px-12 py-5 text-lg transition-colors"
              >
                JOIN PREMIUM NOW
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
